import React, { useState, useCallback } from "react";
import { Home, GraduationCap, Target, Users, ClipboardList, Check } from "lucide-react";
import Sheet from "../shared/Sheet";
import { groupOf } from "../constants/sidebarGroups";
import { haptic } from "../../utils/nativeBridge";
import { GOLD } from "../constants/theme";

/**
 * MobileBottomNav — iOS-style tab bar pinned to the bottom on phones.
 *
 * Why: at <700px the hamburger drawer is the only way to switch between
 * sections. Coaches use these
 * constantly; opening a drawer for each switch is painful and reads
 * webview-y. A bottom tab bar is the universal native pattern.
 *
 * Implementation notes:
 *   - Rendered above the AskBAM floating button; AskBAM lifts itself
 *     above the bar via `.bam-has-bottom-nav` on <body>.
 *   - Main content gets bottom padding via the same body class so the
 *     last items in a scroll list aren't covered.
 *   - Hidden via media query at ≥700px (desktop has the sidebar).
 *   - Frosted-glass background works on iOS (Safari + WKWebView both
 *     support backdrop-filter) and falls back to solid on older
 *     browsers.
 *   - Active state: gold icon + label + small gold dot above icon.
 *     Selection haptic on tap (Apple's own apps do this).
 */

// The phone's version of the grouped sidebar (constants/sidebarGroups.js):
// Home, Community, Learn, Drills, Coach. A group tab opens a sheet of its
// pages rather than guessing which one you meant; Learn opens its own page. Chats moved to the orb,
// with its unread count; My Portal is in the drawer under "You".
const TABS = [
  { id: "dashboard", label: "Home",      Icon: Home },
  { id: "community", label: "Community", Icon: Users },
  { group: "g-learn",  label: "Learn",   Icon: GraduationCap },
  { group: "g-drills", label: "Drills",  Icon: Target },
  { group: "g-coach",  label: "Coach",   Icon: ClipboardList },
];

const inGroup = (groups, gid, nav) => {
  if (typeof nav !== "string") return false;
  const base = nav.split("/")[0];
  const g = groups.find(x => x.id === gid);
  return !!g && g.items.some(i => i.id === base);
};

export function MobileBottomNav({ nav, onNavigate, groups = [] }) {
  const [sheet, setSheet] = useState(null);
  const close = useCallback(() => setSheet(null), []);
  const here = groupOf(nav);
  const openGroup = sheet ? groups.find(g => g.id === sheet) : null;
  return (
    <>
    <nav
      className="bam-mobile-bottom-nav"
      data-tour-target="tabs"
      aria-label="Primary"
      style={{
        position: "fixed",
        left: 0, right: 0, bottom: 0,
        zIndex: 9985,
        display: "none", // shown via CSS media query at <700px
        background: "rgba(10, 10, 10, 0.82)",
        backdropFilter: "saturate(180%) blur(20px)",
        WebkitBackdropFilter: "saturate(180%) blur(20px)",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <div style={{ display: "flex", alignItems: "stretch", height: 58, minHeight: 58 }}>
        {TABS.map((tab) => {
          if (tab.group && !groups.some(g => g.id === tab.group)) return null;
          // A page tab is lit on its route and any sub-route
          // (community/profile/x); a group tab on any page inside it.
          // A group can carry pages that aren't sidebar rows (My Organization
          // and the Intern Hub are tiles on desktop, in Coach's sheet here).
          const active = tab.group
            ? here === tab.group || inGroup(groups, tab.group, nav)
            : nav === tab.id || (typeof nav === "string" && nav.startsWith(tab.id + "/"));
          const color = active ? GOLD : "rgba(255,255,255,0.55)";
          return (
            <button
              key={tab.id || tab.group}
              type="button"
              onClick={() => {
                const g = tab.group && groups.find(x => x.id === tab.group);
                // A group with its own page (Learn) opens that page; the rest
                // open a sheet of their pages.
                if (g && g.home) onNavigate(g.home);
                else if (g) { haptic("selection"); setSheet(tab.group); }
                else onNavigate(tab.id);
              }}
              aria-current={active ? "page" : undefined}
              aria-haspopup={tab.group ? "dialog" : undefined}
              {...(tab.group === "g-learn" ? { "data-tour-target": "learn-tab", "data-moved": "learn" } : {})}
              {...(tab.group === "g-drills" ? { "data-moved": "nav" } : {})}
              aria-label={tab.label}
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "8px 4px 6px",
                fontFamily: "'DM Sans', sans-serif",
                color,
                position: "relative",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              {/* Tiny gold dot above the icon when active — subtle but
                  reads as "you are here" without dominating the chrome. */}
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  top: 4,
                  width: 4, height: 4, borderRadius: "50%",
                  background: active ? GOLD : "transparent",
                  transition: "background .15s ease",
                }}
              />
              <span style={{ position: "relative", display: "flex" }}>
                <tab.Icon size={22} strokeWidth={active ? 2.2 : 1.8} color={color}/>
              </span>
              <span style={{ fontSize: 10.5, fontWeight: active ? 700 : 500, letterSpacing: 0.2 }}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
    {/* Its own stacking layer, or the sheet (z 5000) opens under the bar. */}
    {openGroup && (
      <div style={{ position: "relative", zIndex: 9992 }}>
      <Sheet C={SHEET_C} onClose={close} label={openGroup.label}>
        {/* A plain list, the way iOS Settings draws one (Coleman picked it
            from the canvas, 2026-10-10): the group's name as a title with
            Done, then the places as rows. No eyebrow, no icon tiles, no
            subtitle under every row; the page you're on gets a check. */}
        <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "flex-end",
          minHeight: 44, margin: "-4px 0 8px" }}>
          <div style={{ position: "absolute", left: 0, right: 0, textAlign: "center", pointerEvents: "none",
            fontSize: 17, fontWeight: 600, color: "#F2F2F2" }}>{openGroup.label}</div>
          <button type="button" onClick={close}
            style={{ position: "relative", minHeight: 44, padding: "0 4px", border: "none", background: "transparent",
              color: GOLD, fontFamily: "inherit", fontSize: 17, fontWeight: 600, cursor: "pointer" }}>Done</button>
        </div>
        <div style={{ background: "#1E1E1E", borderRadius: 10, overflow: "hidden" }}>
          {openGroup.items.map((item, i) => {
            const active = nav === item.id || (typeof nav === "string" && nav.startsWith(item.id + "/"));
            const disabled = !!item.comingSoon;
            return (
              <React.Fragment key={item.id}>
                {i > 0 && <div aria-hidden="true" style={{ height: 1, background: "rgba(255,255,255,0.08)", marginLeft: 52 }}/>}
                <button type="button" disabled={disabled} aria-current={active ? "page" : undefined}
                  onClick={() => { close(); onNavigate(item.id); }}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, minHeight: 52, padding: "0 16px",
                    border: "none", background: "transparent", textAlign: "left", fontFamily: "inherit",
                    color: disabled ? "#6A6A6A" : "#F2F2F2", fontSize: 17, fontWeight: 400,
                    cursor: disabled ? "default" : "pointer" }}>
                  <item.Icon size={22} strokeWidth={1.7} color={disabled ? "#555" : GOLD}/>
                  <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
                  {disabled && <span style={{ fontSize: 15, color: "#6A6A6A" }}>Soon</span>}
                  {active && !disabled && <Check size={18} strokeWidth={2.2} color={GOLD} aria-hidden="true"/>}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </Sheet>
      </div>
    )}
    </>
  );
}

const SHEET_C = { bg: "#111", border: "rgba(255,255,255,0.08)" };

export default MobileBottomNav;
